import React, { useEffect, useState } from "react";
import { PageHeader } from "./Pengumuman";
import { CalendarDays, MapPin, Clock, User, Plus, Edit3, Trash2, X, ImagePlus, Camera, Loader2 } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { kegiatanAPI, API } from "../lib/api";
import { useApp } from "../context/AppContext";

const empty = { nama: "", tanggal: "", jam: "", lokasi: "", pj: "", image: "", photos: [], deskripsi: "" };

const Kegiatan = () => {
  const { user } = useApp();
  const canEdit = user?.role === "admin" || user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [galleryOpen, setGalleryOpen] = useState(null); // kegiatan being viewed
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [uploading, setUploading] = useState(false);

  const load = () => kegiatanAPI.list().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (k) => { setEditing(k); setForm({ ...empty, ...k, photos: k.photos || [] }); setOpen(true); };

  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await API.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data.url;
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Ukuran maksimal 5MB"); return; }
    setUploading(true);
    try {
      const url = await uploadOne(file);
      setForm((f) => ({ ...f, image: url }));
      toast.success("Cover terunggah");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload gagal"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handlePhotosUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const oversized = files.find((f) => f.size > 5 * 1024 * 1024);
    if (oversized) { toast.error(`"${oversized.name}" > 5MB`); return; }
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const u = await uploadOne(f);
        urls.push(u);
      }
      setForm((f) => ({ ...f, photos: [...(f.photos || []), ...urls] }));
      toast.success(`${urls.length} foto terunggah`);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload gagal"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const removePhoto = (idx) => setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.nama || !form.tanggal) { toast.error("Nama & tanggal wajib diisi"); return; }
    try {
      if (editing) { await kegiatanAPI.update(editing.id, form); toast.success("Kegiatan diperbarui"); }
      else { await kegiatanAPI.create(form); toast.success("Kegiatan ditambahkan"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const remove = async (k) => {
    if (!window.confirm(`Hapus kegiatan "${k.nama}"?`)) return;
    try { await kegiatanAPI.remove(k.id); toast.success("Kegiatan dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <div>
      <PageHeader title="Kegiatan" subtitle="Agenda dan dokumentasi kegiatan warga DM Residence" icon={CalendarDays}
        action={canEdit && <Button className="brand-btn" onClick={openNew} data-testid="kegiatan-add-btn"><Plus className="h-4 w-4 mr-1" />Tambah Kegiatan</Button>}
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((k) => (
          <Card key={k.id} className="card-hover overflow-hidden group">
            {k.image && <div className="relative">
              <img src={k.image} alt={k.nama} className="w-full h-44 object-cover" />
              {k.photos?.length > 0 && (
                <button onClick={() => { setGalleryOpen(k); setGalleryIndex(0); }} className="absolute bottom-2 right-2 flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/60 hover:bg-black/80 text-white text-xs font-semibold backdrop-blur transition" data-testid={`kegiatan-gallery-${k.id}`}>
                  <Camera className="h-3.5 w-3.5" />{k.photos.length + 1} foto
                </button>
              )}
            </div>}
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-bold text-slate-800 flex-1" style={{ fontFamily: "Poppins, sans-serif" }}>{k.nama}</h3>
                {canEdit && <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(k)} data-testid={`kegiatan-edit-${k.id}`}><Edit3 className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-700" onClick={() => remove(k)} data-testid={`kegiatan-delete-${k.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>}
              </div>
              {k.deskripsi && <p className="text-sm text-slate-600 mb-3 line-clamp-2">{k.deskripsi}</p>}
              <div className="space-y-1.5 text-sm text-slate-600">
                <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-emerald-600" />{k.tanggal}</div>
                <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-emerald-600" />{k.jam} WIB</div>
                <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-emerald-600" />{k.lokasi}</div>
                <div className="flex items-center gap-2"><User className="h-4 w-4 text-emerald-600" />PJ: {k.pj}</div>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <div className="col-span-full text-center py-16 text-slate-400">Belum ada kegiatan tercatat</div>}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Kegiatan" : "Tambah Kegiatan Baru"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama Kegiatan *</Label><Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} placeholder="Kerja Bakti Bulanan" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tanggal *</Label><Input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} /></div>
              <div><Label>Jam</Label><Input type="time" value={form.jam} onChange={(e) => setForm({ ...form, jam: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Lokasi</Label><Input value={form.lokasi} onChange={(e) => setForm({ ...form, lokasi: e.target.value })} placeholder="Depan Rumah RT" /></div>
              <div><Label>Penanggung Jawab</Label><Input value={form.pj} onChange={(e) => setForm({ ...form, pj: e.target.value })} placeholder="Bpk. Ahmad" /></div>
            </div>
            <div><Label>Deskripsi</Label><Textarea rows={3} value={form.deskripsi} onChange={(e) => setForm({ ...form, deskripsi: e.target.value })} placeholder="Rincian kegiatan..." /></div>

            <div>
              <Label>Foto Cover (utama)</Label>
              <div className="flex items-center gap-3 mt-1">
                {form.image ? <img src={form.image} alt="cover" className="h-20 w-32 object-cover rounded-md border" /> : <div className="h-20 w-32 rounded-md border border-dashed grid place-items-center text-slate-400"><ImagePlus className="h-5 w-5" /></div>}
                <div>
                  <input type="file" accept="image/*" id="cover" className="hidden" onChange={handleCoverUpload} disabled={uploading} />
                  <Label htmlFor="cover" className={`cursor-pointer inline-block px-3 py-2 rounded-md brand-btn text-white text-sm ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
                    {uploading ? <><Loader2 className="h-3 w-3 animate-spin inline mr-1" />Mengunggah...</> : "Unggah Cover"}
                  </Label>
                  {form.image && !uploading && <Button variant="ghost" size="sm" className="ml-2 text-red-600" onClick={() => setForm({ ...form, image: "" })}>Hapus</Button>}
                </div>
              </div>
              <div className="text-xs text-slate-500 mt-1">Atau paste URL:</div>
              <Input value={form.image || ""} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://..." />
            </div>

            <div>
              <Label>Galeri Foto Kegiatan</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {form.photos.map((p, i) => (
                  <div key={`${p.slice(0, 40)}-${i}`} className="relative group aspect-square rounded-md overflow-hidden border">
                    <img src={p} alt={`foto ${i + 1}`} className="w-full h-full object-cover" />
                    <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-red-600 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition" title="Hapus foto"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
                <label className={`aspect-square rounded-md border-2 border-dashed border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 grid place-items-center cursor-pointer transition ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handlePhotosUpload} disabled={uploading} data-testid="kegiatan-photos-upload" />
                  <div className="text-center">
                    {uploading ? <Loader2 className="h-6 w-6 text-emerald-600 mx-auto animate-spin" /> : <ImagePlus className="h-6 w-6 text-emerald-600 mx-auto" />}
                    <div className="text-xs text-emerald-700 mt-1 font-semibold">{uploading ? "Mengunggah..." : "Tambah Foto"}</div>
                  </div>
                </label>
              </div>
              <div className="text-xs text-slate-500 mt-1">Pilih beberapa foto sekaligus. Maks 2MB per foto.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="brand-btn" onClick={save} data-testid="kegiatan-save-btn">{editing ? "Simpan Perubahan" : "Tambahkan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gallery lightbox */}
      <Dialog open={!!galleryOpen} onOpenChange={(v) => !v && setGalleryOpen(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>{galleryOpen?.nama}</DialogTitle></DialogHeader>
          {galleryOpen && (
            <div>
              <div className="rounded-xl overflow-hidden bg-slate-900 aspect-video">
                <img src={[galleryOpen.image, ...(galleryOpen.photos || [])][galleryIndex]} alt="" className="w-full h-full object-contain" />
              </div>
              <div className="flex gap-2 mt-3 overflow-x-auto scrollbar-thin pb-1">
                {[galleryOpen.image, ...(galleryOpen.photos || [])].filter(Boolean).map((p, i) => (
                  <button key={`${p.slice(0, 40)}-${i}`} onClick={() => setGalleryIndex(i)} className={`h-16 w-20 shrink-0 rounded-md overflow-hidden border-2 transition ${galleryIndex === i ? "border-emerald-500" : "border-transparent hover:border-emerald-300"}`}>
                    <img src={p} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default Kegiatan;
